import type { IAdViewImage } from "@finn-no/adview-provider-frontend-types";
import { useLingui } from "@lingui/react";
import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import { type KeyboardEventHandler, type RefObject, type UIEventHandler, useEffect, useMemo, useRef } from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { InCarouselAdvertisingPlacement } from "../advertising/AdvertisingPlacement.tsx";
import { Actions } from "./Actions.tsx";
import "./image-gallery.css";
import { ImageMissing } from "./ImageMissing.tsx";
import { MockupAd } from "./MockupAd.tsx";
import { GalleryProvider, useGallery } from "./state.tsx";
import { generateSrcSet, scrollAdIntoView, scrollIntoView, useDebounce } from "./util.ts";

// Set to true for to show mockup advertisement banner in local env
const enableMockupAdLocally = false;

const getVisibleImageIndex = (listRef: RefObject<HTMLUListElement | null>, images: IAdViewImage[]) => {
  const initialX = listRef.current?.getBoundingClientRect().x ?? Number.MAX_VALUE;
  let closestX = Number.MAX_VALUE;
  let closestXIndex = -1;

  images.forEach((_image, index) => {
    const imageX = document.getElementById(`gallery-image-${index}`)?.getBoundingClientRect().x ?? Number.MAX_VALUE;
    const diff = Math.abs(imageX - initialX);
    if (diff < closestX) {
      closestX = diff;
      closestXIndex = index;
    }
  });

  const adX = document.getElementById("gallery-ad")?.getBoundingClientRect().x ?? Number.MAX_VALUE;
  const adDiff = Math.abs(adX - initialX);
  if (adDiff < closestX) {
    closestX = adDiff;
    closestXIndex = images.length;
  }

  return closestXIndex;
};

const distanceToCurrentImage = (currentImageIndex: number, index: number, length: number) => {
  const distance1 = Math.abs(currentImageIndex - index);
  const distance2 = Math.abs(currentImageIndex + length - index);

  return Math.min(distance1, distance2);
};

const Gallery = () => {
  const { i18n } = useLingui();
  const ad = useAdViewAd();
  const { images, title } = useAdViewAd();
  const { trackClick } = usePulse();
  const { isMobile, env } = useAppConfig();
  const useMockupAd = enableMockupAdLocally && env === "local";
  const isScrolling = useRef(false);
  const isSyncingScroll = useRef(false);

  const listRef = useRef<HTMLUListElement>(null);
  const debounce = useDebounce();

  const {
    currentImageIndex,
    setCurrentImageIndex,
    isOnAdSlide,
    setIsOnAdSlide,
    hasOpenedFullscreen,
    inCarouselAdLoaded,
    setInCarouselAdLoaded,
    nonFullscreenAdLoaded,
    setNonFullscreenAdLoaded,
    setFullScreen,
    fullScreen,
  } = useGallery();

  const carOfTheYear = "usedCarOfTheYear" in ad ? ad.usedCarOfTheYear : undefined;

  const onScroll: UIEventHandler<HTMLUListElement> = () => {
    debounce(() => {
      isScrolling.current = false;

      // Scroll events triggered by the mount/remount sync below just move the DOM to
      // match the already-correct state, so they must not feed back into setCurrentImageIndex.
      if (isSyncingScroll.current) {
        isSyncingScroll.current = false;
        return;
      }

      if (listRef.current) {
        const visibleIndex = getVisibleImageIndex(listRef, images);
        if (visibleIndex === -1) return;

        const onAdSlide = visibleIndex >= images.length;
        if (onAdSlide !== isOnAdSlide) {
          setIsOnAdSlide(onAdSlide);
        }
        if (!onAdSlide && visibleIndex !== currentImageIndex) {
          setCurrentImageIndex(visibleIndex);
        }
      }
    }, 50);
  };

  const isScrollLockedForNavigation = (keyEvent: string) => {
    return (
      isScrolling.current &&
      ((keyEvent === "ArrowRight" && currentImageIndex !== 0) ||
        (keyEvent === "ArrowLeft" && currentImageIndex !== images.length - 1))
    );
  };

  const onKeyDown: KeyboardEventHandler<HTMLUListElement> = (event) => {
    if (images.length > 0 && !fullScreen && (event.key === " " || event.key === "space")) {
      event.preventDefault();
      setFullScreen(true);
    }

    if (isScrollLockedForNavigation(event.key)) {
      event.preventDefault();
      return;
    }
    isScrolling.current = true;
  };

  useEffect(() => {
    if (listRef.current) {
      const visibleIndex = getVisibleImageIndex(listRef, images);
      const expectedIndex = isOnAdSlide ? images.length : currentImageIndex;
      if (visibleIndex !== -1 && visibleIndex !== expectedIndex) {
        isSyncingScroll.current = true;
        if (isOnAdSlide) {
          scrollAdIntoView();
        } else {
          scrollIntoView(currentImageIndex);
        }
      }
    }
  }, [currentImageIndex, isOnAdSlide, images]);

  // Focus the scrolling region when entering fullscreen for keyboard users
  // biome-ignore lint/correctness/useExhaustiveDependencies: The currentImageIndex regrabs focus after animation to allow arrow key navigation
  useEffect(() => {
    if (fullScreen) {
      listRef.current?.focus();
    }
  }, [fullScreen, currentImageIndex]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only re-sync when an ad actually loads, not on every currentImageIndex/images change (that's handled by the effect above)
  useEffect(() => {
    // The ad slide goes from `display:none` (zero width, out of flow) to a full-width flex
    // item the moment it loads. That layout shift can trigger a real browser scroll event
    // (e.g. scroll anchoring), which the index->scroll sync effect above doesn't cover since
    // it only runs on currentImageIndex/images changes. Re-assert the current slide here so
    // that shift never gets misread as the user navigating to the ad.
    if (!listRef.current) return;
    isSyncingScroll.current = true;
    if (isOnAdSlide) {
      scrollAdIntoView();
    } else {
      scrollIntoView(currentImageIndex);
    }
  }, [inCarouselAdLoaded, nonFullscreenAdLoaded]);

  const openFullscreenLabel = i18n.t({
    id: "itemPageLayout.imageGallery.gallery.fullscreen",
    message: "Open fullscreen image view",
  });

  const imageLabel = i18n.t({
    id: "imageGallery.image.ariaLabel",
    message: `Ad image_`,
  });

  const ariaLabel = useMemo(() => {
    const currentImage = images[currentImageIndex];
    let label = "";
    if (currentImage?.description) {
      label += `${currentImage.description}${currentImage.description?.endsWith(".") ? " " : ". "}`;
    }
    label += i18n.t({
      id: "imageGallery.description",
      message: "Image {current} of {total}",
      values: { current: currentImageIndex + 1, total: images.length },
    });
    return label;
  }, [images, currentImageIndex, i18n]);

  const openFullscreen = () => {
    setFullScreen(true);
    // Tracking event: CE_ev07
    trackClick({
      name: "Open image gallery",
      objectId: "CE_ev07",
      elementType: ElementType.Image,
      intent: Intent.Open,
    });
  };

  const aspectRatio = getAspectRatio(images[0]);

  return (
    <section
      className={`relative flex justify-center items-center overflow-hidden h-full z-0 ${fullScreen ? "mb-16" : "mt-16 md:mt-32"}`}
    >
      <h2 className="sr-only">{i18n.t({ id: "imageGallery.title", message: "Image gallery" })}</h2>
      <div className="s-text-inverted-static">
        <Actions />
      </div>
      <div className="w-full h-full">
        <ul
          aria-live="polite"
          aria-label={ariaLabel}
          ref={listRef}
          className={`flex hide-scrollbar h-full w-full overflow-x-auto ${fullScreen ? "" : "s-border	border rounded-8 mb-0"}`}
          style={{
            scrollSnapType: "x mandatory",
          }}
          onScroll={onScroll}
          onKeyDown={onKeyDown}
        >
          {images.length > 0 ? (
            <>
              {images.map((image, index) => {
                const isCloseToBeingShown = distanceToCurrentImage(currentImageIndex, index, images.length) <= 2;
                return (
                  <li
                    style={{
                      scrollSnapAlign: "start",
                      flexShrink: "0",
                    }}
                    className="w-full relative"
                    key={image.uri}
                    role={!fullScreen ? "button" : undefined}
                    tabIndex={!fullScreen ? 0 : undefined}
                    /* Only apply openFullscreenLabel to index 0 so Playwright strict mode targets a unique element */
                    aria-label={!fullScreen && index === 0 ? openFullscreenLabel : undefined}
                    onClick={!fullScreen ? openFullscreen : undefined}
                    onKeyDown={(e) => {
                      if (!fullScreen && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        openFullscreen();
                      }
                    }}
                  >
                    <img
                      className="w-full h-full cursor-pointer relative z-10 object-contain"
                      style={{
                        aspectRatio,
                        backgroundColor: fullScreen ? "transparent" : "rgba(255, 255, 255, 0.5)",
                      }}
                      id={`gallery-image-${index}`}
                      srcSet={generateSrcSet(image.uri)}
                      alt={image.description || `${title} ${1 + index}`}
                      aria-label={image.description || `${imageLabel} ${1 + index}`}
                      loading={isCloseToBeingShown ? undefined : "lazy"}
                    />
                    {carOfTheYear?.logo?.uri && index === 0 && (
                      <img
                        src={carOfTheYear.logo.uri}
                        alt={carOfTheYear.logo.altText ?? ""}
                        aria-hidden={!(carOfTheYear.logo.altText && carOfTheYear.logo.altText.trim())}
                        className="absolute top-12 md:top-20 left-12 md:left-20 z-20 h-64 w-64 md:h-[88] md:w-[88] pointer-events-none"
                        style={{ objectFit: "contain" }}
                      />
                    )}
                    {!fullScreen ? (
                      <img
                        aria-hidden="true"
                        className="absolute left-0 top-0 w-full h-full z-0 overflow-hidden object-cover object-center"
                        style={{
                          filter: "blur(20px)",
                        }}
                        srcSet={generateSrcSet(image.uri)}
                        alt=""
                        loading={isCloseToBeingShown ? undefined : "lazy"}
                      />
                    ) : null}
                  </li>
                );
              })}

              {fullScreen && (
                // biome-ignore lint/correctness/useUniqueElementIds: We're using the id to be able to grab the element and scroll to it
                <li
                  key="gallery-ad"
                  id="gallery-ad"
                  style={{ scrollSnapAlign: "start" }}
                  className={`w-full shrink-0 relative cursor-default ${inCarouselAdLoaded ? "block" : "hidden"}`}
                  onKeyDown={(e) => e.stopPropagation()} // clicking ad shouldn't advance
                >
                  <div className="w-full h-full flex items-center justify-center">
                    {useMockupAd ? (
                      <MockupAd onLoaded={(loaded) => setInCarouselAdLoaded((prev) => prev || loaded)} />
                    ) : (
                      <InCarouselAdvertisingPlacement
                        images={images}
                        currentImageIndex={currentImageIndex}
                        onLoaded={(loaded) => setInCarouselAdLoaded((prev) => prev || loaded)}
                      />
                    )}
                  </div>
                </li>
              )}

              {/* 2. NON-FULLSCREEN AD SLIDE */}
              {/* Hidden for good once fullscreen has been opened: entering fullscreen unmounts
                  this ad and remounting it afterwards reuses a stale GAM slot for AdvtIncarousel,
                  leaving an empty ad box instead of no box at all. */}
              {!fullScreen && !isMobile && !hasOpenedFullscreen && (
                // biome-ignore lint/correctness/useUniqueElementIds: required for DOM element query targeting
                <li
                  key="gallery-ad-nonfullscreen"
                  id="gallery-ad"
                  style={{ scrollSnapAlign: "start" }}
                  className={`w-full min-w-full shrink-0 relative cursor-default pointer-events-auto ${nonFullscreenAdLoaded ? "block" : "hidden"}`}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <div className="w-full h-full min-h-[300px] flex items-center justify-center">
                    {useMockupAd ? (
                      <MockupAd
                        onLoaded={(loaded) => setNonFullscreenAdLoaded((prev) => prev || loaded)}
                        onClick={openFullscreen}
                      />
                    ) : (
                      <InCarouselAdvertisingPlacement
                        images={images}
                        currentImageIndex={currentImageIndex}
                        onLoaded={(loaded) => setNonFullscreenAdLoaded((prev) => prev || loaded)}
                      />
                    )}
                  </div>
                </li>
              )}
            </>
          ) : (
            <ImageMissing />
          )}
        </ul>
      </div>
    </section>
  );
};

export const ImageGallery = () => {
  return (
    <GalleryProvider>
      <Gallery />
    </GalleryProvider>
  );
};

const getAspectRatio = (image: IAdViewImage | undefined) => {
  const widthFactor = 3;
  const heightFactor = 2;
  const wiggleRoom = 10;

  // The aspect ratio might not exactly match 3:2 so we allow for a +-10 wiggle room
  if (image?.width && image.height && Math.abs(image.width * heightFactor - image.height * widthFactor) < wiggleRoom) {
    return "3/2";
  }
  return "4/3";
};
